import {
  GameRenderer,
  StageData as RendererStageData,
} from "@/game/renderer/GameRenderer";
import { Header } from "@/ui/Header";
import { Footer } from "@/ui/Footer";
import { CardPalette } from "@/ui/CardPalette";
import { ProgramArea } from "@/ui/ProgramArea";
import { SubbookPanel, type SubbookTheme } from "@/ui/SubbookPanel";
import { SubbookSelectorModal } from "@/ui/SubbookSelectorModal";
import { OrbSelectorModal } from "@/ui/OrbSelectorModal";
import { TurnSelectorModal } from "@/ui/TurnSelectorModal";
import { StepSelectorModal } from "@/ui/StepSelectorModal";
import { ConditionSelectorModal } from "@/ui/ConditionSelectorModal";
import { ControlPanel } from "@/ui/ControlPanel";
import { StageSelector } from "@/ui/StageSelector";
import { MPDisplay } from "@/ui/MPDisplay";
import { VariableDisplay } from "@/ui/VariableDisplay";
import { FloorTabs } from "@/ui/FloorTabs";
import { insertMagicCircle } from "@/ui/MagicCircle";
import { ResultModal } from "@/ui/ResultModal";
import { Executor, ExecutionResult } from "@/program/Executor";
import {
  Card,
  CardType,
  createCard,
  getCardCost,
  TURN_DIRECTION_VALUES,
} from "@/program/Card";
import { StageData, StageVariant, STAGES } from "@/game/stages/StageData";
import { ThemeName } from "@/game/map/ThemeConfig";

// ─── 現在のステージデータ ───
let currentStage: StageData = STAGES[0];

// ─── ゲーム描画 ───
const gameContainer = document.getElementById("game-container");
const gameRenderer = gameContainer ? new GameRenderer(gameContainer) : null;

const withTheme = (
  data: StageVariant | StageData
): RendererStageData & { theme?: ThemeName; stageId?: number } => ({
  ...data,
  theme: currentStage.theme,
  stageId: currentStage.id,
});

gameRenderer?.render(withTheme(currentStage));

// ─── 結果メッセージ ───
const RESULT_MESSAGES: Record<ExecutionResult, string> = {
  cleared: "クリア！",
  not_cleared: "宝に到達できませんでした",
  step_limit: "ステップ数の上限に達しました",
};

// ─── DOM要素の取得 ───
const headerEl = document.getElementById("header");
const footerEl = document.getElementById("footer");
const gameColumn = document.getElementById("game-column");
const rightColumn = document.getElementById("right-column");

if (!headerEl || !footerEl || !gameColumn || !rightColumn) {
  throw new Error("Required DOM elements not found");
}

// ─── Header / Footer ───
const header = new Header(headerEl);
new Footer(footerEl);
header.setDescription(currentStage.description);

// ─── StageSelector（Header内） ───
const stageSelector = new StageSelector(header.stageWrap);

// ─── Info Bar（game-column内、game-containerの前） ───
const infoBar = document.createElement("div");
infoBar.className = "info-bar";
gameColumn.insertBefore(infoBar, gameColumn.firstChild);

// オーブ表示（info-cardでラップ）
const orbCard = document.createElement("div");
orbCard.className = "info-card info-card--orbs";
const orbLabel = document.createElement("span");
orbLabel.className = "info-card__label";
orbLabel.textContent = "オーブ";
orbCard.appendChild(orbLabel);
const variableDisplay = new VariableDisplay(orbCard);
infoBar.appendChild(orbCard);

// ステップ数表示
const stepsCard = document.createElement("div");
stepsCard.className = "info-card info-card--steps";
const stepsLabel = document.createElement("span");
stepsLabel.className = "info-card__label";
stepsLabel.textContent = "ステップ";
stepsCard.appendChild(stepsLabel);
const stepsValue = document.createElement("span");
stepsValue.className = "info-card__value";
stepsValue.textContent = "—";
stepsCard.appendChild(stepsValue);
infoBar.appendChild(stepsCard);

// ─── FloorTabs（game-column内） ───
let floorTabs: FloorTabs | null = null;
floorTabs = new FloorTabs(gameColumn, gameContainer ?? undefined);

// ─── game-wrap + game-frame（game-containerをラップ） ───
const gameWrap = document.createElement("div");
gameWrap.className = "game-wrap";
const gameFrame = document.createElement("div");
gameFrame.className = "game-frame";
if (gameContainer) {
  gameFrame.appendChild(gameContainer);
}
gameWrap.appendChild(gameFrame);
gameColumn.appendChild(gameWrap);

// ─── Right Column の構築 ───

// palette-bar
const paletteBar = document.createElement("div");
paletteBar.className = "palette-bar";
rightColumn.appendChild(paletteBar);

const cardPalette = new CardPalette(paletteBar);

// tab-bar（魔導書 / サブブックA / B / C）
const tabBar = document.createElement("div");
tabBar.className = "tab-bar";
tabBar.setAttribute("role", "tablist");
tabBar.setAttribute("aria-label", "ブック選択");
const tabGrimoire = document.createElement("button");
tabGrimoire.id = "tab-grimoire";
tabGrimoire.className = "tab-bar__tab tab-bar__tab--active";
tabGrimoire.textContent = "魔導書";
tabGrimoire.setAttribute("role", "tab");
tabGrimoire.setAttribute("aria-selected", "true");
tabGrimoire.setAttribute("aria-controls", "panel-grimoire");
const tabSubbookA = document.createElement("button");
tabSubbookA.id = "tab-subbook-a";
tabSubbookA.className = "tab-bar__tab";
tabSubbookA.textContent = "サブブックA";
tabSubbookA.setAttribute("role", "tab");
tabSubbookA.setAttribute("aria-selected", "false");
tabSubbookA.setAttribute("aria-controls", "panel-subbook-a");
const tabSubbookB = document.createElement("button");
tabSubbookB.id = "tab-subbook-b";
tabSubbookB.className = "tab-bar__tab";
tabSubbookB.textContent = "サブブックB";
tabSubbookB.setAttribute("role", "tab");
tabSubbookB.setAttribute("aria-selected", "false");
tabSubbookB.setAttribute("aria-controls", "panel-subbook-b");
const tabSubbookC = document.createElement("button");
tabSubbookC.id = "tab-subbook-c";
tabSubbookC.className = "tab-bar__tab";
tabSubbookC.textContent = "サブブックC";
tabSubbookC.setAttribute("role", "tab");
tabSubbookC.setAttribute("aria-selected", "false");
tabSubbookC.setAttribute("aria-controls", "panel-subbook-c");
tabBar.appendChild(tabGrimoire);
tabBar.appendChild(tabSubbookA);
tabBar.appendChild(tabSubbookB);
tabBar.appendChild(tabSubbookC);
rightColumn.appendChild(tabBar);

// 初期状態: タブバー非表示（subbookなしステージ）
tabBar.style.display = "none";

// ─── grimoire テーマ定義 ───
const GRIMOIRE_THEME: SubbookTheme = {
  inkColor: "#5a3a1a",
  patternType: "pentagram",
  edgeSeed: 3,
};

const SUBBOOK_A_THEME: SubbookTheme = {
  inkColor: "#306830",
  patternType: "hexagram",
  edgeSeed: 11,
};

const SUBBOOK_B_THEME: SubbookTheme = {
  inkColor: "#1e3a5f",
  patternType: "heptagram",
  edgeSeed: 17,
};

const SUBBOOK_C_THEME: SubbookTheme = {
  inkColor: "#8b2020",
  patternType: "octagram",
  edgeSeed: 7,
};

type ActiveTab = "grimoire" | "subbook-a" | "subbook-b" | "subbook-c";

const SUBBOOK_THEMES: Record<Exclude<ActiveTab, "grimoire">, SubbookTheme> = {
  "subbook-a": SUBBOOK_A_THEME,
  "subbook-b": SUBBOOK_B_THEME,
  "subbook-c": SUBBOOK_C_THEME,
};

// grimoire（羊皮紙エリア）
const grimoire = document.createElement("div");
grimoire.className = "grimoire";
rightColumn.appendChild(grimoire);
insertMagicCircle(grimoire);

// ProgramArea / SubbookPanel（grimoire内）
const programArea = new ProgramArea(grimoire);
const subbookPanelA = new SubbookPanel(grimoire, "subbook-a", "サブブックA");
const subbookPanelB = new SubbookPanel(grimoire, "subbook-b", "サブブックB");
const subbookPanelC = new SubbookPanel(grimoire, "subbook-c", "サブブックC");
const subbookPanels = [subbookPanelA, subbookPanelB, subbookPanelC];

// WAI-ARIA タブパネル設定
programArea.configureTabPanel("panel-grimoire", "tab-grimoire");
subbookPanelA.configureTabPanel("panel-subbook-a", "tab-subbook-a");
subbookPanelB.configureTabPanel("panel-subbook-b", "tab-subbook-b");
subbookPanelC.configureTabPanel("panel-subbook-c", "tab-subbook-c");

// タブ切替ロジック
let activeTab: ActiveTab = "grimoire";

const applyGrimoireTheme = (theme: SubbookTheme): void => {
  grimoire.style.setProperty("--grimoire-ink", theme.inkColor);

  // 魔法陣を差し替え
  const existing = grimoire.querySelector(".grimoire__magic-circle");
  if (existing) existing.remove();
  insertMagicCircle(grimoire, theme.patternType);

  // ラフエッジマスクを seed 変更して再適用
  currentMaskSeed = theme.edgeSeed;
  applyGrimoireMask(grimoire, theme.edgeSeed);
};

const switchTab = (tab: ActiveTab) => {
  activeTab = tab;

  const tabDefs: { btn: HTMLButtonElement; id: ActiveTab }[] = [
    { btn: tabGrimoire, id: "grimoire" },
    { btn: tabSubbookA, id: "subbook-a" },
    { btn: tabSubbookB, id: "subbook-b" },
    { btn: tabSubbookC, id: "subbook-c" },
  ];
  for (const { btn, id } of tabDefs) {
    btn.classList.toggle("tab-bar__tab--active", tab === id);
    btn.setAttribute("aria-selected", String(tab === id));
  }

  // ProgramArea: show/hide
  if (tab === "grimoire") {
    programArea.show();
  } else {
    programArea.hide();
  }

  // SubbookPanel: show/hide
  if (tab === "subbook-a") {
    subbookPanelA.show();
    subbookPanelB.hide();
    subbookPanelC.hide();
  } else if (tab === "subbook-b") {
    subbookPanelA.hide();
    subbookPanelB.show();
    subbookPanelC.hide();
  } else if (tab === "subbook-c") {
    subbookPanelA.hide();
    subbookPanelB.hide();
    subbookPanelC.show();
  } else {
    subbookPanelA.hide();
    subbookPanelB.hide();
    subbookPanelC.hide();
  }

  // grimoire テーマ切り替え
  applyGrimoireTheme(tab !== "grimoire" ? SUBBOOK_THEMES[tab] : GRIMOIRE_THEME);
};

tabGrimoire.addEventListener("click", () => switchTab("grimoire"));
tabSubbookA.addEventListener("click", () => switchTab("subbook-a"));
tabSubbookB.addEventListener("click", () => switchTab("subbook-b"));
tabSubbookC.addEventListener("click", () => switchTab("subbook-c"));

// 初期状態: 全SubbookPanelを非表示
subbookPanels.forEach((p) => p.hide());

// subbookSlots に応じてタブバー・タブボタンの表示/非表示を更新
const updateTabVisibility = (slots: number): void => {
  tabBar.style.display = slots > 0 ? "" : "none";
  tabSubbookA.hidden = slots < 1;
  tabSubbookB.hidden = slots < 2;
  tabSubbookC.hidden = slots < 3;
};

// result-area（MP警告テキスト用）
const resultArea = document.createElement("div");
resultArea.className = "result-area";
resultArea.setAttribute("aria-live", "polite");
rightColumn.appendChild(resultArea);

// bottom-bar（MP + 実行 + クリア）
const bottomBar = document.createElement("div");
bottomBar.className = "bottom-bar";
rightColumn.appendChild(bottomBar);

const mpDisplay = new MPDisplay(bottomBar);
const controlPanel = new ControlPanel(bottomBar);

// ─── ResultModal ───
const resultModal = new ResultModal();

// ─── SubbookSelectorModal ───
const subbookSelectorModal = new SubbookSelectorModal();
subbookSelectorModal.onSelect((subbookId) => {
  if (activeTab !== "grimoire") {
    // サブブックタブがアクティブならそのパネルに追加
    const activePanel =
      activeTab === "subbook-a"
        ? subbookPanelA
        : activeTab === "subbook-b"
          ? subbookPanelB
          : subbookPanelC;

    const callCost = getCardCost(
      createCard("call", undefined, undefined, subbookId)
    );
    const allSubbookCost = subbookPanels.reduce(
      (sum, p) => sum + p.getCurrentCost(),
      0
    );
    if (
      programArea.getTotalCost() + allSubbookCost + callCost >
      currentStage.mp
    ) {
      resultArea.textContent = "MPが足りません";
      resultArea.className = "result-area result-area--warning";
      isShowingMPWarning = true;
      return;
    }
    activePanel.addCard("call", subbookId);
    if (isShowingMPWarning) clearResult();
  } else {
    const added = programArea.addCard("call", subbookId);
    if (!added) {
      resultArea.textContent = "MPが足りません";
      resultArea.className = "result-area result-area--warning";
      isShowingMPWarning = true;
    } else if (isShowingMPWarning) {
      clearResult();
    }
  }
});

// ─── 即モーダル（パレットクリック時） ───
const turnSelectorModal = new TurnSelectorModal();
const stepSelectorModal = new StepSelectorModal();
const conditionSelectorModal = new ConditionSelectorModal();
const orbSelectorModal = new OrbSelectorModal();

// ─── 初期ステージのMP設定 ───
programArea.setMaxMP(currentStage.mp);
mpDisplay.update(0, currentStage.mp);

// ─── MP集計 ───
let isUpdatingMP = false;
const updateTotalMP = () => {
  if (isUpdatingMP) return;
  isUpdatingMP = true;
  const subbookCost = subbookPanels.reduce(
    (sum, p) => sum + p.getCurrentCost(),
    0
  );
  const total = programArea.getTotalCost() + subbookCost;
  programArea.setMaxMP(currentStage.mp - subbookCost);
  mpDisplay.update(total, currentStage.mp);
  isUpdatingMP = false;
};

programArea.onMPChange(() => updateTotalMP());
subbookPanels.forEach((p) => p.onCostChange(() => updateTotalMP()));

// ─── ターゲットモード連携 ───
subbookPanels.forEach((panel) => {
  panel.onActivate(() => {
    programArea.setActiveContainer(null, false);
    subbookPanels.forEach((p) => {
      if (p !== panel) p.deactivate();
    });
  });
});

programArea.onTargetChange(() => {
  subbookPanels.forEach((p) => p.deactivate());
});

// ─── 結果表示 ───
let isShowingMPWarning = false;
let stepCount = 0;

const clearResult = () => {
  resultArea.textContent = "";
  resultArea.className = "result-area";
  isShowingMPWarning = false;
};

const resetStepCount = () => {
  stepCount = 0;
  stepsValue.textContent = "—";
};

// ─── ステージ選択 ───
stageSelector.onSelect((stage) => {
  currentStage = stage;
  programArea.clear();
  subbookPanels.forEach((p) => p.clear());
  variableDisplay.reset();
  clearResult();
  resetStepCount();
  header.setDescription(stage.description);

  // タブバー・サブブック表示切替
  const slots = stage.subbookSlots ?? 0;
  updateTabVisibility(slots);
  if (slots > 0) {
    cardPalette.showCallCard();
  } else {
    cardPalette.hideCallCard();
  }
  switchTab("grimoire");

  programArea.setMaxMP(stage.mp);
  programArea.setSubbookSlots(stage.subbookSlots ?? 3);
  mpDisplay.update(0, stage.mp);

  const variants = stage.variants;
  if (floorTabs) {
    if (variants && variants.length > 1) {
      floorTabs.setFloors(variants);
      floorTabs.show();
    } else {
      floorTabs.hide();
    }
  }

  gameRenderer?.render(withTheme(currentStage));
});

// ─── フロアタブクリック ───
if (floorTabs) {
  floorTabs.onFloorSelect((index) => {
    const variants = currentStage.variants;
    if (!variants || index >= variants.length) return;
    gameRenderer?.render(withTheme(variants[index]));
  });
}

// ─── パレットカード追加ヘルパー ───

/** クリック/ドロップ時点でのアクティブパネルへのカード追加関数を返す */
function makeAddFn(
  targetArea: ActiveTab
): (card: Card, insertionIndex?: number) => void {
  if (targetArea !== "grimoire") {
    const panel =
      targetArea === "subbook-a"
        ? subbookPanelA
        : targetArea === "subbook-b"
          ? subbookPanelB
          : subbookPanelC;
    return (card: Card) => {
      const allSubbookCost = subbookPanels.reduce(
        (s, p) => s + p.getCurrentCost(),
        0
      );
      if (
        programArea.getTotalCost() + allSubbookCost + getCardCost(card) >
        currentStage.mp
      ) {
        resultArea.textContent = "MPが足りません";
        resultArea.className = "result-area result-area--warning";
        isShowingMPWarning = true;
        return;
      }
      panel.addCardDirect(card);
      if (isShowingMPWarning) clearResult();
    };
  }
  return (card: Card, insertionIndex?: number) => {
    const added = programArea.addCardDirect(card, insertionIndex);
    if (!added) {
      resultArea.textContent = "MPが足りません";
      resultArea.className = "result-area result-area--warning";
      isShowingMPWarning = true;
    } else if (isShowingMPWarning) {
      clearResult();
    }
  };
}

/** 利用可能MPを計算 */
function calcAvailableMP(): number {
  const allSubbookCost = subbookPanels.reduce(
    (s, p) => s + p.getCurrentCost(),
    0
  );
  return currentStage.mp - programArea.getTotalCost() - allSubbookCost;
}

// ─── カードクリック共通ハンドラ ───
function handlePaletteCardAdd(
  type: CardType,
  addFn: (card: Card, insertionIndex?: number) => void = makeAddFn(activeTab),
  insertionIndex?: number
): void {
  if (type === "call") {
    subbookSelectorModal.open(currentStage.subbookSlots ?? 3);
    return;
  }

  if (type === "forward") {
    const availableMP = calcAvailableMP();
    stepSelectorModal.onSelect((sel) => {
      const card =
        sel.type === "steps"
          ? createCard("forward", sel.value)
          : createCard("forward", undefined, sel.color);
      addFn(card, insertionIndex);
    });
    stepSelectorModal.open(null, availableMP);
    return;
  }

  if (type === "turn") {
    turnSelectorModal.onSelect((direction) => {
      addFn(
        createCard("turn", TURN_DIRECTION_VALUES[direction]),
        insertionIndex
      );
    });
    turnSelectorModal.open(null);
    return;
  }

  if (type === "if" || type === "while") {
    conditionSelectorModal.onSelect((condition) => {
      const card = createCard(type);
      card.branches![0].condition = condition;
      addFn(card, insertionIndex);
    });
    conditionSelectorModal.open();
    return;
  }

  if (type === "addToOrb") {
    const availableMP = calcAvailableMP();
    orbSelectorModal.onSelect((sel) => {
      const cardType = sel.operator === "add" ? "addToOrb" : "multiplyOrb";
      addFn(createCard(cardType, sel.value, sel.color), insertionIndex);
    });
    orbSelectorModal.openForNew(availableMP);
    return;
  }

  // action 等: そのまま追加
  addFn(createCard(type), insertionIndex);
}

cardPalette.onCardClick(handlePaletteCardAdd);

// ─── パレット→ブランチボディへのD&D ───
// CardListEngineがactiveContainerIdをbranchIdにセット済み状態で呼ばれる
// activeTabに依存せず常にgrimoireに追加する（サブブックパネルへの誤追加を防ぐ）
// insertionIndex は黄色い線が示すブランチ内の挿入位置
programArea.onPaletteBranchDrop((_branchId, cardType, insertionIndex) => {
  handlePaletteCardAdd(
    cardType as CardType,
    makeAddFn("grimoire"),
    insertionIndex
  );
});

// ─── パレットからのD&Dドロップ（プログラムエリアへ） ───
// D&DはgrimoireエリアへのみFixのため activeTab は必ず "grimoire"
// handlePaletteCardAdd を再利用して calcAvailableMP の計算を統一する
// insertionIndex は黄色い線が示す挿入位置（ドロップ座標から計算）
programArea.onPaletteDrop((type, insertionIndex) => {
  handlePaletteCardAdd(type, makeAddFn("grimoire"), insertionIndex);
});

// ─── 実行 ───
let executor: Executor | null = null;

const executeFloor = (
  floorData: StageVariant,
  cards: ReturnType<typeof programArea.getCards>
): Promise<ExecutionResult> => {
  gameRenderer?.render(withTheme(floorData));

  const subbookDefs = subbookPanels
    .map((p) => p.getDefinition())
    .filter((def) => def.cards.length > 0);

  executor = new Executor(
    gameRenderer!.getFamiliar(),
    gameRenderer!.getTileMap(),
    gameRenderer!.getTreasure(),
    { stepDelay: 300 },
    subbookDefs
  );

  executor.setListener({
    onStepStart: (cardId) => {
      programArea.highlightCard(cardId);
      subbookPanels.forEach((p) => p.highlightCard(cardId));
      stepCount++;
      stepsValue.textContent = String(stepCount);
    },
    onStepEnd: () => {
      programArea.clearHighlight();
      subbookPanels.forEach((p) => p.clearHighlight());
    },
    onFinish: () => {
      programArea.clearHighlight();
      subbookPanels.forEach((p) => p.clearHighlight());
    },
    onVariableChange: (color, value) => variableDisplay.update(color, value),
  });

  variableDisplay.reset();
  return executor.execute(cards);
};

controlPanel.onRun(async () => {
  if (!gameRenderer) return;

  const cards = programArea.getCards();
  if (cards.length === 0) {
    resultArea.textContent = "カードを追加してください";
    resultArea.className = "result-area result-area--warning";
    return;
  }

  controlPanel.setRunning(true);
  variableDisplay.reset();
  clearResult();
  stepCount = 0;
  stepsValue.textContent = "0";

  const variants = currentStage.variants;
  const floors =
    variants && variants.length > 0
      ? variants
      : [
          {
            familiar: currentStage.familiar,
            treasure: currentStage.treasure,
            walls: currentStage.walls,
          },
        ];

  const isMultiFloor = floors.length > 1;
  let allCleared = true;

  if (isMultiFloor) {
    floorTabs?.setInteractive(false);
    floorTabs?.resetStatus();
  }

  for (let i = 0; i < floors.length; i++) {
    if (isMultiFloor) {
      resultArea.textContent = `${i + 1}階 探索中...`;
      resultArea.className = "result-area";
      floorTabs?.setFloorStatus(i, "running");
    }

    const result = await executeFloor(floors[i], cards);

    if (result !== "cleared") {
      controlPanel.setRunning(false);
      const message = isMultiFloor
        ? `${i + 1}階: ${RESULT_MESSAGES[result]}`
        : RESULT_MESSAGES[result];
      resultModal.show(message, "failure");
      if (isMultiFloor) {
        floorTabs?.setFloorStatus(i, "failed");
        floorTabs?.setInteractive(true);
      }
      allCleared = false;
      break;
    }

    if (isMultiFloor) {
      floorTabs?.setFloorStatus(i, "cleared");
      if (i < floors.length - 1) {
        resultArea.textContent = `${i + 1}階 クリア！ 次の階へ...`;
        resultArea.className = "result-area result-area--success";
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
    }
  }

  if (allCleared) {
    controlPanel.setRunning(false);
    resultModal.show(RESULT_MESSAGES["cleared"], "success");
    if (isMultiFloor) {
      floorTabs?.setInteractive(true);
    }
  }
});

// ─── クリア ───
controlPanel.onClear(() => {
  programArea.clear();
  subbookPanels.forEach((p) => p.clear());
  variableDisplay.reset();
  clearResult();
  resetStepCount();
  floorTabs?.resetStatus();
  gameRenderer?.render(withTheme(currentStage));
});

// ─── PC幅スケーリング ───
// 1024px未満のウィンドウ幅では transform: scale でUI全体を縮小する。
// 縦積みレイアウトにしないことでゲームと魔導書の同時表示（コア体験）を維持。
const SCALE_MIN_WIDTH = 1024;
function applyScale(): void {
  const scale = Math.min(1, window.innerWidth / SCALE_MIN_WIDTH);
  if (scale < 1) {
    document.body.style.transform = `scale(${scale})`;
    document.body.style.transformOrigin = "top left";
    document.body.style.width = `${window.innerWidth / scale}px`;
    // height は設定しない: CSSの height:100% (=100vh) に任せることで
    // ゲームカラムが縦に引き伸ばされるのを防ぐ。
    // scale後の表示高さは viewport.height × scale になるが、
    // html 背景色（wood-900）が残りのスペースを自然に埋める。
    document.body.style.height = "";
  } else {
    document.body.style.transform = "";
    document.body.style.transformOrigin = "";
    document.body.style.width = "";
    document.body.style.height = "";
  }
}
const scaleObserver = new ResizeObserver(applyScale);
scaleObserver.observe(document.documentElement);
applyScale();

// ─── grimoire ラフエッジマスク（動的生成）───
// viewBox をその都度 grimoire の実ピクセルサイズに合わせることで
// feDisplacementMap scale がピクセル直結になり、要素サイズに依存しなくなる。
// scale=8 → 変位 ±4px（常にこの値、要素が何pxでも変わらない）。
const MASK_INSET = 10; // rect の端からの余白（px）
const MASK_SCALE = 8; // feDisplacementMap scale → 変位 ±4px 固定
const MASK_BASE_FREQ = 0.012; // 元の 0.015 より少し低く → 元の視覚的粗さに近い

/** 現在のラフエッジ seed（タブ切り替えで更新） */
let currentMaskSeed = GRIMOIRE_THEME.edgeSeed;

function buildGrimoireMask(w: number, h: number, seed: number): string {
  const svg = [
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${w} ${h}'>`,
    `<filter id='r'>`,
    `<feTurbulence type='turbulence' baseFrequency='${MASK_BASE_FREQ}' numOctaves='4' seed='${seed}'/>`,
    `<feDisplacementMap in='SourceGraphic' scale='${MASK_SCALE}'/>`,
    `</filter>`,
    `<rect x='${MASK_INSET}' y='${MASK_INSET}' `,
    `width='${w - MASK_INSET * 2}' height='${h - MASK_INSET * 2}' `,
    `rx='2' fill='white' filter='url(#r)'/>`,
    `</svg>`,
  ].join("");
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

function applyGrimoireMask(grimoire: HTMLElement, seed: number): void {
  const w = Math.round(grimoire.clientWidth);
  const h = Math.round(grimoire.clientHeight);
  if (w === 0 || h === 0) return;
  const mask = buildGrimoireMask(w, h, seed);
  grimoire.style.setProperty("-webkit-mask-image", mask);
  grimoire.style.setProperty("mask-image", mask);
  grimoire.style.setProperty("-webkit-mask-size", "100% 100%");
  grimoire.style.setProperty("mask-size", "100% 100%");
}

const grimoireEl = document.querySelector<HTMLElement>(".grimoire");
if (grimoireEl) {
  const maskObserver = new ResizeObserver(() =>
    applyGrimoireMask(grimoireEl, currentMaskSeed)
  );
  maskObserver.observe(grimoireEl);
  applyGrimoireMask(grimoireEl, currentMaskSeed);
}
